<script setup>
  import { computed, onMounted, onUnmounted, ref, watch } from "vue"

  const props = defineProps({
    publicationDateData: {
      type: Object,
      required: true,
      default: null,
    },
  })

  const route = useRoute()
  const { $dayjs } = useNuxtApp()
  const { locale } = useI18n()
  const { type } = route.params
  const remainingDuration = ref($dayjs.duration(0))
  const countdownInterval = ref(null)
  const platformDialog = ref(null)

  const showPresaleCountdown = computed(() => {
    if (!props.publicationDateData.pre_sale_enabled) return false

    return remainingDuration.value.asMilliseconds() > 0
  })

  function getTargetDate() {
    return $dayjs.tz(
      props.publicationDateData.pre_sale_start_date,
      props.publicationDateData.country_timezone
    )
  }

  function getCurrentTime() {
    return $dayjs().tz(props.publicationDateData.country_timezone)
  }

  function updateCountdown() {
    const target = getTargetDate()
    if (!target.isValid()) return

    const now = getCurrentTime()
    const diff = target.diff(now)

    if (diff <= 0) {
      remainingDuration.value = $dayjs.duration(0)
      clearInterval(countdownInterval.value)
      countdownInterval.value = null
    } else {
      remainingDuration.value = $dayjs.duration(diff)
    }
  }

  function startCountdown() {
    updateCountdown()
    if (!countdownInterval.value) {
      countdownInterval.value = setInterval(updateCountdown, 1000)
    }
  }

  const showExternalPlatform = computed(() => {
    if (
      props.publicationDateData &&
      props.publicationDateData?.show_external_platform_url
    ) {
      return (
        Object.keys(props.publicationDateData?.external_platform).length > 0
      )
    }

    return false
  })

  const buyTickets = computed(() => {
    if (props.publicationDateData?.date_type === "open_ticket") {
      return `/${locale.value}/${type}-tickets/${props.publicationDateData?.id}/tickets`
    } else if (props.publicationDateData?.date_type === "stage") {
      return `/${locale.value}/${type}-tickets/${props.publicationDateData?.id}/seats`
    } else {
      console.error("Publication date type not found")
      return "#"
    }
  })

  onMounted(() => {
    if (
      props.publicationDateData.pre_sale_enabled &&
      props.publicationDateData.pre_sale_start_date &&
      getCurrentTime().isBefore(getTargetDate())
    ) {
      startCountdown()
    }
  })

  onUnmounted(() => {
    if (countdownInterval.value) {
      clearInterval(countdownInterval.value)
    }
  })

  watch(
    () => [
      props.publicationDateData.pre_sale_enabled,
      props.publicationDateData.pre_sale_start_date,
    ],
    ([newEnabled, newStartDate]) => {
      if (
        newEnabled &&
        newStartDate &&
        getCurrentTime().isBefore(getTargetDate())
      ) {
        startCountdown()
      } else {
        if (countdownInterval.value) {
          clearInterval(countdownInterval.value)
          countdownInterval.value = null
        }
      }
    }
  )

  const days = computed(() => Math.floor(remainingDuration.value.asDays()))
  const hours = computed(() => remainingDuration.value.hours())
  const minutes = computed(() => remainingDuration.value.minutes())
  const seconds = computed(() => remainingDuration.value.seconds())

  const formattedHours = computed(() => String(hours.value).padStart(2, "0"))
  const formattedMinutes = computed(() =>
    String(minutes.value).padStart(2, "0")
  )
  const formattedSeconds = computed(() =>
    String(seconds.value).padStart(2, "0")
  )
</script>

<template>
  <template v-if="!showPresaleCountdown">
    <div
      v-if="!!showExternalPlatform"
      class="buy-btn"
      @click="platformDialog.showDialog({
        ...props.publicationDateData?.external_platform,
        external_platform_url: props.publicationDateData?.external_platform_url,
        external_platform_id: props.publicationDateData?.external_platform_id
      })"
    >
      <span>Comprar Ahora</span>
      <svg class="w-[32px]" viewBox="0 0 35 35" fill="none">
        <path
          d="M5.83325 16.0417V18.9583H23.3333L15.3124 26.9792L17.3833 29.05L28.9333 17.5L17.3833 5.95001L15.3124 8.02085L23.3333 16.0417H5.83325Z"
          fill="white"
        />
      </svg>
    </div>
    <NuxtLink
      v-else
      :to="buyTickets"
      :target="publicationDateData?.external_platform_url ? '_blank' : '_self'"
      class="buy-btn"
      :class="{
        disabled:
          props.publicationDateData.pre_sale_enabled &&
          getCurrentTime().isBefore(getTargetDate()),
      }"
    >
      <span>Comprar Ahora</span>
      <svg class="w-[32px]" viewBox="0 0 35 35" fill="none">
        <path
          d="M5.83325 16.0417V18.9583H23.3333L15.3124 26.9792L17.3833 29.05L28.9333 17.5L17.3833 5.95001L15.3124 8.02085L23.3333 16.0417H5.83325Z"
          fill="white"
        />
      </svg>
    </NuxtLink>
  </template>
  <div class="buy-btn" v-if="showPresaleCountdown">
    <div class="flex justify-center items-center gap-x-3">
      <div class="text-center" v-if="days > 0">
        <div class="counter-number">{{ days }}</div>
        <div class="counter-label">Días</div>
      </div>
      <hr
        class="inline w-[1px] h-auto bg-white opacity-20 shrink-0 self-stretch border-none"
        aria-orientation="vertical"
        v-if="days > 0"
      />
      <div class="text-center">
        <div class="counter-number">{{ formattedHours }}</div>
        <div class="counter-label">Horas</div>
      </div>
      <hr
        class="inline w-[1px] h-auto bg-white opacity-20 shrink-0 self-stretch border-none"
        aria-orientation="vertical"
      />
      <div class="text-center">
        <div class="counter-number">{{ formattedMinutes }}</div>
        <div class="counter-label">Minutos</div>
      </div>
      <hr
        class="inline w-[1px] h-auto bg-white opacity-20 shrink-0 self-stretch border-none"
        aria-orientation="vertical"
      />
      <div class="text-center">
        <div class="counter-number">{{ formattedSeconds }}</div>
        <div class="counter-label">Segundos</div>
      </div>
    </div>
    <svg class="w-[32px]" viewBox="0 0 35 35" fill="none">
      <path
        d="M5.83325 16.0417V18.9583H23.3333L15.3124 26.9792L17.3833 29.05L28.9333 17.5L17.3833 5.95001L15.3124 8.02085L23.3333 16.0417H5.83325Z"
        fill="white"
      />
    </svg>
  </div>
  <div
    v-if="showPresaleCountdown"
    class="text-center text-gray-300 text-bold mt-3 text-[13px]"
  >
    Disponible el:
    {{ $dayjs(getTargetDate()).tz($dayjs.tz.guess()).format("dddd[,] DD MMMM YYYY [a las] hh:mm a") }}
  </div>

  <ExternalPlatformDialog ref="platformDialog" />
</template>

<style scoped lang="scss">
  .buy-btn {
    @apply flex items-center justify-between cursor-pointer dark:bg-primary-500 bg-[#1C1C1C] text-white font-semibold py-3 px-6 rounded-xl transition-all hover:shadow-lg hover:shadow-[#dd99a9] dark:hover:shadow-primary-700;
    @apply w-full;
    user-select: none;
    &.disabled {
      pointer-events: none;
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .counter-number {
    font-size: 1.2rem;
    font-weight: bold;
    color: #fff;
    line-height: 1.3;
  }

  .counter-label {
    font-size: 0.75rem;
    line-height: 1;
    color: #ccc;
  }
</style>
